import {Inject, Injectable} from '@angular/core';
import {DOCUMENT} from "@angular/common";
import {HttpClient} from '@angular/common/http';
import {StateService} from '@uirouter/core';
import {BehaviorSubject, Observable, of, Subscription, zip} from "rxjs";

import {AppAlertService} from "./app-alert.service";
import {ControlService} from './tiles';
import {ParametersService} from './parameters.service';
import {SubtitlesService} from "./subtitles.service";
import {UserService} from './user.service';
import {
    MainMenu,
    MenuDate,
    MenuElement,
    MenuElementType12,
    MenuHelper,
    MenuParent,
    MenuParentElement,
    MenuResponse,
    MenuType,
    MenuTypeKey,
    ModuleStatus,
    PinData,
    ScheduleMenuElement
} from "../models";

const VALID_PASS = 'validPass';

@Injectable({
    providedIn: 'root',
})
export class MenuService {
    allBreadcrumbs: MenuElement[] = [];
    allElementMenu: MenuElement[] | {} | undefined = undefined;
    breadcrumbFirst: MenuElement | unknown[] | '' = [];
    downloaded: boolean = false;
    elementMenu: MenuElement;
    private isMenuRequesting: boolean = false;
    lastUpdate: string = '';
    mainMenu: MainMenu | {} = {};
    private menuHelper: MenuHelper = {
        value: '',
        brotherElement: [],
    };
    menuPromise: Observable<Array<MenuResponse>> | undefined;
    parents: MenuParent[] = [];
    parentElementaa: MenuElement | 0 | undefined;
    password: string = '';
    passwordElement: MenuElement | '' = '';
    passwordQuery: string[] = [];
    pins: PinData[] = [];
    runMenu: boolean = false;
    search = {txt: ''};
    showDropdownMenu: boolean = false;
    showInputField: boolean = false;
    smallBreadcrumbs: boolean = false;
    submenu: MenuElement[] | {} = {};
    private tempValWiki: MenuElement | 0 = 0;
    wikiInfo: string = '';
    wikiTitle: string = '';
    updatedMenu = new BehaviorSubject<boolean>(false);

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private appAlert: AppAlertService,
        private control: ControlService,
        private http: HttpClient,
        private parameters: ParametersService,
        private subtitles: SubtitlesService,
        private $state: StateService,
        private user: UserService,
    ) {
        this.pins = this.getPinsFromLocalStorage();
    }

    private createBreadcrumbs(val: MenuElement, tab: MenuElement[]): MenuElement[] {
        const allBreadcrumbs: MenuElement[] = [];

        for (let i = 0; i < tab.length; i++) {
            if (val.parentId == tab[i].id && tab[i].id !== 0) {
                allBreadcrumbs.unshift(val);
                val = tab[i];
                i = 0;
            }
        }
        allBreadcrumbs.unshift(val);

        return allBreadcrumbs;
    }

    clearMenu(): void {
        this.search = {txt: ''};
        this.submenu = {};
        this.allBreadcrumbs = [];
        this.breadcrumbFirst = [];
        this.showInputPassword(false);
        this.tempValWiki = 0;
        this.wikiInfo = '';
        this.control.tileControl = undefined;

        for (const key in this.mainMenu) {
            this.mainMenu[key].isVisible = false;
        }
    }

    createMenuQuery(menuType: MenuType, udid: string | 0): string {
        if (this.pins.length === 0) return menuType;

        const pinData = this.pins.find(pinData => {
            if (pinData.id === 0 && pinData.menuType === menuType && pinData.udid === udid) {
                return pinData;
            }
        });

        return pinData ? `${menuType}/0:${pinData.pin}` : menuType;
    }

    getMenuPromise(moduleUdid?: string | 0): Observable<Array<MenuResponse>> {
        let udid = moduleUdid ?? this.parameters.selectedModuleHash;

        if (udid === 0) return of([]);

        this.isMenuRequesting = true;

        const baseUrl = `/api/v1/users/${this.user.user_id}/modules/${udid}`;
        const promises = [];

        const fittersMenuPromise = this.http.get(`${baseUrl}/menu/${this.createMenuQuery(MenuType.MI, udid)}`);
        promises.push(fittersMenuPromise);
        const serviceMenuPromise = this.http.get(`${baseUrl}/menu/${this.createMenuQuery(MenuType.MS, udid)}`);
        promises.push(serviceMenuPromise);
        const userMenuPromise = this.http.get(`${baseUrl}/menu/${this.createMenuQuery(MenuType.MU, udid)}`);
        promises.push(userMenuPromise);
        const manufacturerMenuPromise = this.http.get(`${baseUrl}/menu/${this.createMenuQuery(MenuType.MP, udid)}`);
        promises.push(manufacturerMenuPromise);

        return zip(promises);
    }

    getMenuElements(responses: MenuResponse[]): {
        allElementMenu: MenuElement[],
        parents: MenuParent[]
    } {
        const [fittersMenuData, serviceMenuData, userMenuData, manufacturerMenuData] = responses;

        const tempMenu = {
            MI: fittersMenuData?.data?.elements ?? [],
            MS: serviceMenuData?.data?.elements ?? [],
            MU: userMenuData?.data?.elements ?? [],
            MP: manufacturerMenuData?.data?.elements ?? [],
        }
        const allElementMenu = [].concat(tempMenu.MI, tempMenu.MS, tempMenu.MU, tempMenu.MP);
        this.parents = [];

        allElementMenu.forEach(element => {
            if (element.type === 0 && element.params.password || element.id === 0 && element.menuType === MenuType.MS) {
                this.parents.push({type: element.menuType, id: element.id})
            }
        });

        return {
            allElementMenu,
            parents: this.parents,
        }
    }

    getMainMenu(): Subscription {
        if (this.isMenuRequesting) return;

        this.menuPromise = this.getMenuPromise();

        return this.menuPromise.subscribe((responses: Array<MenuResponse>) => {
            this.isMenuRequesting = false;
            const {allElementMenu, parents} = this.getMenuElements(responses);

            const dates: MenuDate[] = [];
            responses.forEach((response: MenuResponse) => {
                const dateString = response.data?.transaction_time;

                if (dateString) {
                    dates.push({date: new Date(dateString), dateString})
                }
            })

            const minDateData = dates.reduce((acc, cv) => {
                if (acc.minDate === null || cv.date.getTime() < acc.minDate.getTime()) {
                    acc = {dateString: cv.dateString, minDate: cv.date}
                }
                return acc
            }, {dateString: '', minDate: null});

            this.parents = parents;
            this.allElementMenu = allElementMenu;
            this.lastUpdate = minDateData.dateString;
            this.downloaded = true;

            this.setMainMenu();
            this.updatedMenu.next(true);
        });
    }

    setMainMenu(): void {
        if (this.allElementMenu === undefined || !Array.isArray(this.allElementMenu)) return;

        this.smallBreadcrumbs = window.innerWidth < 1366;
        const mainMenu: MainMenu = {
            MU: [],
            MS: [],
            MI: [],
            MP: [],
        };
        let showInMenu = false;

        for (const val of this.allElementMenu) {
            showInMenu = false;

            if (val.access) {
                const parent = this.allElementMenu.find((parent: MenuElement) =>
                    this.isMenuElementParent(val, parent)
                );
                showInMenu = parent?.access ?? false;
            }

            if (showInMenu) {
                switch (val.menuType) {
                    case MenuType.MI:
                        if (val.id === 0) {
                            mainMenu.MI.unshift(val);
                        } else {
                            mainMenu.MI.push(val);
                        }
                        break;
                    case MenuType.MU:
                        if (val.id === 0) {
                            mainMenu.MU.unshift(val);
                        } else {
                            mainMenu.MU.push(val);
                        }
                        break;
                    case MenuType.MP:
                        if (val.id === 0) {
                            mainMenu.MP.unshift(val);
                        } else {
                            mainMenu.MP.push(val);
                        }
                        break;
                    case MenuType.MS:
                        if (val.id === 0) {
                            mainMenu.MS.unshift(val);
                        } else {
                            mainMenu.MS.push(val);
                        }
                        break;
                }
            }
        }

        Object.keys(mainMenu).forEach(key => {
            const val = mainMenu[key];

            if (val.length === 0 || mainMenu[key][0].id !== 0 || !mainMenu[key][0].access) {
                delete mainMenu[key];
            }
        });

        this.mainMenu = mainMenu;
    }

    private isMenuElementParent(element: MenuElement, parent: MenuElement): boolean {
        return element.parentId === parent.id
            && element.menuType === parent.menuType;
    }

    getElementMenu(id: number): void {
        this.elementMenu = undefined;

        for (const key in this.allElementMenu) {
            if (id === this.allElementMenu[key].id) {
                this.elementMenu = this.allElementMenu[key];
            }
        }
    }

    checkSelectedMenu(element: MenuType): MenuTypeKey | false {
        switch (element) {
            case MenuType.MU:
                return MenuTypeKey.MU;
            case MenuType.MI:
                return MenuTypeKey.MI;
            case MenuType.MS:
                return MenuTypeKey.MS;
            case MenuType.MP:
                return MenuTypeKey.MP;
            default:
                return false;
        }
    }

    goToMenuByType(type: MenuType): void {
        switch (type) {
            case 'MU':
                this.$state.go('menu', {selectedModuleHash: this.parameters.selectedModuleHash});
                break;
            case 'MI':
                this.$state.go('fitters_menu', {selectedModuleHash: this.parameters.selectedModuleHash});
                break;
            case 'MS':
                this.$state.go('service_menu', {selectedModuleHash: this.parameters.selectedModuleHash});
                break;
            case 'MP':
                this.$state.go('manufacturer_menu', {selectedModuleHash: this.parameters.selectedModuleHash});
                break;
        }
    }

    goToMenu(element: MenuElement[], selectedModule: ModuleStatus): void {
        this.control.comeFromControl = false;
        this.goToMenuByType(element[0].menuType);
        this.showSubmenu(element, selectedModule);
    }

    showSubmenu(element: MenuElement[], selectedModule: ModuleStatus): void | false {
        if (!(selectedModule.moduleStatus && selectedModule.controllerStatus) || this.parameters.offline) {
            return false;
        }

        this.showInputPassword(false);
        this.showDropdownMenu = false;
        this.tempValWiki = 0;
        this.wikiInfo = '';
        this.menuHelper.value = '';
        this.menuHelper.brotherElement = [];
        this.search = {txt: ''};

        if (this.hasParamsPassword(element[0]) && element[0].params.password === 'password') {
            this.showInputPassword(element[0]);
        } else {
            for (const key in this.mainMenu) {
                this.mainMenu[key].isVisible = false;
            }
            this.mainMenu[element[0].menuType].isVisible = true;
            this.getMenuChild(element[0]);
        }
    }

    getMenuChild(element: MenuElement | 0, windowWidth?: number): boolean {
        this.password = '';
        this.tempValWiki = 0;
        this.wikiInfo = '';
        this.showInputPassword(false);
        this.setBreadcrumbsScroll();

        if (windowWidth <= 900) this.getBreadcrumbs(element);

        if (element !== 0 && element.id === 0) this.allBreadcrumbs = [];

        if (element !== 0) this.breadcrumbFirst = this.mainMenu[element.menuType][0];

        if (element !== 0 && element.type !== 0 && element.type !== 35) {
            this.control.hideMenu = true;
            this.control.brotherElement = this.getBrotherElement(element, this.mainMenu[element.menuType]);
            this.control.pin = this.getParentPin(element);
            this.allBreadcrumbs.pop();
            this.control.setControl(element);
            this.control.fromMenu = true;
            if (this.isSchedulePlusMinusMenuElement(element)){
                this.control.setTemp = this.searchSetTemp(element);
            }
            return false;
        } else if (element !== 0 && element.params.password === 'password') {
            this.showInputPassword(element);
            return false;
        } else if (element !== 0 && Object.keys(this.search).length && element.type !== 0) {
            this.search.txt = this.search.txt.length > 1 ? '' : this.search.txt;
        } else {
            this.getMenuChildElement(element);
            this.search.txt = '';
        }

        if (element !== 0 && element.params.password === VALID_PASS && element.id !== 0) {
            element.params.password = 'password';
        }
    }

    getParentPin(element: MenuElement): string {
        const parent = this.getMenuParentElement(element)

        if (parent && (parent.id === 0 || parent.type === 35)) {
            const pinData = this.getPinData(parent, this.parameters.selectedModuleHash);
            return pinData ? pinData.pin : ''
        }

        if (this.hasParamsPassword(parent)) {
            return Array.isArray(parent.params.password) ? parent.params.password[0] : '';
        }
        return '';
    }

    getMenuChildElement(element: MenuElement | 0 = 0): void {
        if (element === 0) return;

        this.parentElementaa = element;
        const menuType = element.menuType;
        const submenu: MenuElement[] = [];

        for (const val of this.mainMenu[menuType]) {
            if (element.id === val.parentId && val.id !== 0 && val.access === true) {
                submenu.push(val);
            }
        }

        this.submenu = submenu;
    }

    private getBrotherElement(element: MenuElement, menuArray: MenuElement[]): MenuElement[] | false {
        if (!this.isScheduleMenuElement(element)) {
            return false;
        }

        if (element.params.dayId < 7 && element.params.nextObjId > 0) {
            const table: ScheduleMenuElement[] = [];
            let tmpDay = element.params.nextObjId > 0 ? element.params.nextObjId : element.id;

            for (let i = 0; i < menuArray.length; i++) {
                const element = menuArray[i];

                if (this.isScheduleMenuElement(element) && element.id === tmpDay) {
                    table.push(element);
                    tmpDay = element.params.nextObjId > 0 ? element.params.nextObjId :element.id + 1;
                    i = 0;

                    if (table.length === 7) {
                        const sortTable = table.slice(0);

                        sortTable.sort(function (a, b) {
                            const x = a.id;
                            const y = b.id;

                            return x < y ? -1 : x > y ? 1 : 0;
                        });
                        return sortTable;
                    }
                }
            }
        }
        return false;
    }

    getBreadcrumbs(element: MenuElement | 0): MenuElement[] {
        let allBreadcrumbs = this.allBreadcrumbs;

        if (element === 0) {
          allBreadcrumbs.pop();
        } else {
            if (element) {
                allBreadcrumbs = this.createBreadcrumbs(element, this.mainMenu[element.menuType]);
            }
        }

        this.allBreadcrumbs = allBreadcrumbs;

        return allBreadcrumbs;
    }

    updateElementMenu(element: MenuElement): void {
        if (!Array.isArray(this.allElementMenu)) return;

        let index: number = this.allElementMenu.findIndex((item: MenuElement) => item.id === element.id);
        if (index >= 0) this.allElementMenu[index] = element;

        if (!Array.isArray(this.submenu)) return;
        if (this.submenu.length) {
            index = this.submenu.findIndex((item: MenuElement) => item.id === element.id);
            if (index >= 0) this.submenu[index] = element;
        }

        index = this.mainMenu[element.menuType].findIndex((item: MenuElement) => item.id === element.id);
        if (index >= 0) this.mainMenu[element.menuType][index] = element;
    }

    searchText(searchText: string, element: MenuElement[]): void {
        this.tempValWiki = 0;
        this.wikiInfo = '';

        if (searchText.length > 1) {
            this.searchTextInArray(searchText, element);
        } else if (searchText.length === 0) {
            this.submenu = [];
            this.getMenuChild(element[0]);
        }

        this.search.txt = searchText;
    }

    private searchTextInArray(searchText: string, element: MenuElement[]): void {
        const submenu = [];
        searchText = searchText.toLowerCase();

        for (const val of this.mainMenu[element[0].menuType]) {
            if (val.id !== 0) {
                let sub = this.subtitles.setSubtitle(val.txtId) ?? '';

                if (val.type === 13 && val.params.daySubtitles === 0) {
                    sub = this.setDayName(val.params.dayId);
                }

                sub = sub.toLowerCase();

                if (sub.indexOf(searchText) >= 0) {
                    for (const parentVal of this.mainMenu[element[0].menuType]) {
                        if (val.parentId === parentVal.id && parentVal.access) {
                            submenu.push(val);
                        }
                    }
                }
            }
        }

        this.submenu = submenu;
    }

    private setBreadcrumbsScroll(): true {
        const elem = this.document.querySelectorAll('.breadcrumbs');

        elem.forEach((val: Element) => {
            val.scrollTop = val.scrollHeight;
        })

        return true;
    }

    goBack(element: MenuElement): void {
        this.tempValWiki = 0;
        this.wikiInfo = '';
        this.getBreadcrumbs(element);

        if (!element) {
            if (this.allBreadcrumbs.length) {
                const element = this.allBreadcrumbs[this.allBreadcrumbs.length - 1];
                let parent = this.allBreadcrumbs.find(menuElement => menuElement.id === element.parentId);

                if (!parent && Array.isArray(this.allElementMenu)) {
                    parent = this.allElementMenu.find(
                        menuElement => menuElement.id === element.parentId
                            && menuElement.menuType === element.menuType
                    );
                }

                this.getMenuChild(parent);
                return;
            }

            this.clearMenu();
            this.$state.go('home', {selectedModuleHash: this.parameters.selectedModuleHash});
        } else {
            let breadcrumbLevel = this.allBreadcrumbs.length;
            if (element.parentId !== 0 && !this.search.txt) {
                if ((breadcrumbLevel - 3) < 0) {
                    this.getMenuChild(this.mainMenu[element.menuType][0]);
                    this.allBreadcrumbs = [];
                } else {
                    breadcrumbLevel = breadcrumbLevel - 3;
                    this.getBreadcrumbs(this.allBreadcrumbs[breadcrumbLevel]);

                    const breadcrumbElement = this.allBreadcrumbs[breadcrumbLevel];
                    if (this.hasParamsPassword(breadcrumbElement) && breadcrumbElement.params.password) {
                        breadcrumbElement.params.password = VALID_PASS;
                        this.allBreadcrumbs[breadcrumbLevel] = breadcrumbElement;
                    }
                    this.getMenuChild(this.allBreadcrumbs[breadcrumbLevel]);
                }
            } else {
                this.clearMenu();
                this.$state.go('home', {selectedModuleHash: this.parameters.selectedModuleHash});

                for (const key in this.mainMenu) {
                    this.mainMenu[key].isVisible = false;
                    this.breadcrumbFirst = '';
                    this.allBreadcrumbs = [];
                }
            }
        }
    }

    private getMenuParentElement(element: MenuElement): MenuElement | undefined {
        if (Array.isArray(this.allElementMenu)) {
            return this.allElementMenu.find(menuElement =>
                menuElement.id === element.parentId && menuElement.menuType === element.menuType
            )
        }
    }

    addPin(element: MenuElement | "", udid: string | 0): void {
        if (element === '' || udid === 0) return;
        const pinData = this.getPinData(element, udid);

        if (!pinData) {
            this.pins.push({
                menuType: element.menuType,
                pin: this.password,
                id: element.id,
                udid,
            });

            if (element.id === 0 && element.parentId === 0) {
                localStorage.setItem('pins', JSON.stringify(this.pins));
            }
        }
    }

    getPinData(element: MenuElement, udid: string | 0): PinData {
        return this.pins.find(pinData =>
            pinData.menuType === element.menuType &&
            pinData.id === element.id &&
            pinData.udid === udid
        );
    }

    createMenuElementQuery(element: MenuElement, udid: string | 0, menuElementParent?: MenuElement): string | undefined {
        if (!element) return;
        if (this.password === '') return '';

        let newQuery = `${element.id}:${this.password}`;
        const pinData = this.getPinData(element, udid);
        if (pinData) {
            newQuery = `${pinData.id}:${pinData.pin}`;
        }

        const hasQuery = this.passwordQuery.find(query => query === newQuery);
        if (!hasQuery) {
            this.passwordQuery.push(newQuery);
        }

        if (element.id === 0 && element.parentId === 0) {
            const queryArray = this.passwordQuery.reverse();
            return queryArray.join(',');
        } else {
            const parentElement = menuElementParent ?? this.getMenuParentElement(element);

            return this.createMenuElementQuery(parentElement, udid);
        }
    }

    usePassword(): void {
        const baseUrl = `/api/v1/users/${this.user.user_id}/modules/${this.parameters.selectedModuleHash}`;
        this.password = this.password.replace(/\D/g, '');

        if (this.passwordElement === "") return;
        const {menuType} = this.passwordElement;
        const query = this.createMenuElementQuery(this.passwordElement, this.parameters.selectedModuleHash);

        if (query === '') {
            this.appAlert.showAppAlert(this.subtitles.setSubtitle(4074),
                this.subtitles.setSubtitle(1136), null, false, null);
            return;
        }

        this.http.get(`${baseUrl}/menu/${menuType}/${query}`).subscribe({
            next: (response: any) => {
                this.passwordQuery = [];
                this.addPin(this.passwordElement, this.parameters.selectedModuleHash);

                if (response.data.elements.length !== 0) {
                    this.submenu = [];

                    for (const element of response.data.elements) {
                        if (Object.keys(element).length === 0) continue;

                        if (this.passwordElement !== "" && this.passwordElement.id === element.parentId && element.access === true) {
                            if (Array.isArray(this.submenu)) {
                                this.submenu.push(element);
                            }
                        }
                        this.mainMenu = this.indexOfObject(element, this.mainMenu);

                        if (!this.indexOfPasswordElement(element) && Array.isArray(this.allElementMenu)) {
                            this.allElementMenu.push(element);
                        }
                    }

                    if (this.passwordElement !== ""
                        && this.passwordElement.id === 0
                        && this.hasParamsPassword(this.passwordElement)) {
                        this.passwordElement.params.password = VALID_PASS;

                        const selectedModuleStatus: ModuleStatus = {
                            moduleStatus: true,
                            controllerStatus: true,
                        }

                        this.showSubmenu(this.mainMenu[this.passwordElement.menuType], selectedModuleStatus);
                    }

                    this.showInputPassword(false);
                    this.password = '';
                } else {
                    this.password = '';
                }
            },
            error: (response) => {
                if (response.status === 422) {
                    this.password = '';
                    this.passwordQuery = [];

                    this.appAlert.showAppAlert(this.subtitles.setSubtitle(4074),
                        this.subtitles.setSubtitle(1136), null, false, null);
                }
            }
        });
    }

    private indexOfPasswordElement(element: MenuElement): boolean {
        let result = false;

        if (!Array.isArray(this.allElementMenu)) return false;
        for (const val of this.allElementMenu) {
            if (element.id === val.id && element.menuType === val.menuType) {
                result = true;
            }
        }
        return result;
    };

    private indexOfObject(element: MenuElement, mainMenu: MainMenu): MainMenu {
        const index = this.mainMenu[element.menuType].findIndex(
            (val: MenuElement) => element.id === val.id && element.parentId === val.parentId);

        if (index === -1) {
            mainMenu[element.menuType].push(element);
        }

        return mainMenu;
    };

    showInputPassword(element: MenuElement | false | -1): void {
        if ((element === -1) && (this.passwordElement !== '' && this.passwordElement.id === 0)) {
            this.showInputField = false;
            this.passwordElement = '';

            this.$state.go('home', {selectedModuleHash: this.parameters.selectedModuleHash});
        } else if (element === -1 || !element) {
            this.showInputField = false;
            this.passwordElement = '';
        } else {
            this.passwordElement = element;
            this.showInputField = true;
            if (element.id === 0) {
                for (const key in this.mainMenu) {
                    this.mainMenu[key].isVisible = false;
                    this.breadcrumbFirst = '';
                }
            }
        }
    };

    showWiki(element: MenuElement | 0): void {
        if ((this.tempValWiki !== element) && (element !== 0)) {
            this.tempValWiki = element;
            this.wikiInfo = this.subtitles.setWiki(element.wikiTxtId);
            this.wikiTitle = this.subtitles.setSubtitle(element.txtId);
        } else {
            this.wikiInfo = '';
            this.tempValWiki = 0;
        }
    };

    setDayName(val: number): string {
        switch (val) {
            case 0:
                return this.subtitles.setSubtitle(445);
            case 1:
                return this.subtitles.setSubtitle(590);
            case 2:
                return this.subtitles.setSubtitle(894);
            case 3:
                return this.subtitles.setSubtitle(708);
            case 4:
                return this.subtitles.setSubtitle(235);
            case 5:
                return this.subtitles.setSubtitle(516);
            case 6:
                return this.subtitles.setSubtitle(703);
            case 7:
                return this.subtitles.setSubtitle(588);
            case 8:
                return this.subtitles.setSubtitle(701);
            case 9:
                return this.subtitles.setSubtitle(818);
            default:
                return '';
        }
    };

    getPinsForUpdate(): string[] {
        return this.pins.length > 0
            ? this.pins
                .filter(pinData => pinData.udid === this.parameters.selectedModuleHash)
                .map(({menuType, id, pin}) => `${menuType}:${id}:${pin}`)
            : [];
    }

    private searchSetTemp(element: MenuElementType12): MenuElement | 0 {
        let setTemp: MenuElement | 0 = 0;

        for (const key in this.mainMenu) {
            const menuArray: MenuElement[] = this.mainMenu[key];

            for (const val of menuArray) {
                if (element.params.valueObjId === val.id) {
                    setTemp = val;
                }
            }
        }

        return setTemp;
    };

    private getPinsFromLocalStorage(): PinData[] {
        const pins = localStorage.getItem('pins');

        return pins === null ? [] : JSON.parse(pins);
    }

    hasParamsPassword(element: MenuElement): element is MenuParentElement {
        return element.type === 0 || element.type === 35;
    }

    private isScheduleMenuElement(element: MenuElement): element is ScheduleMenuElement {
        return element.type === 12 || element.type === 13 || element.type === 100;
    }

    private isSchedulePlusMinusMenuElement(element: MenuElement): element is MenuElementType12 {
        return element.type === 12;
    }
}
